import React, {useCallback, useMemo} from "react";
import {FormControl, FormHelperText, InputLabel, ListItemText, MenuItem, Select} from "@mui/material";
import {useSelector} from "react-redux";
import {languageSelector, languagesSelector} from "../../state/app/appSelectors";
import {localizeI18nObj} from "../../utils/i18n";

/**
 * Builds a tree from a flat list and flattens it depth-first,
 * annotating each node with its level and whether it is a leaf.
 * @param {Array} items - Flat list of items
 * @param {string} idKey - Property name for the unique identifier
 * @param {string} labelKey - Property name for the display label
 * @param {string} parentKey - Property name for the parent reference
 */
function buildAndFlatten(items, idKey, labelKey, parentKey) {
  const map = new Map();

  // prepare nodes with children
  items.forEach(item => {
    if (!item || item[idKey] == null) return;
    map.set(item[idKey], {id: item[idKey], label: item[labelKey], children: []});
  });

  const roots = [];

  // link parent → children
  items.forEach(item => {
    if (!item || item[idKey] == null) return;
    const node = map.get(item[idKey]);
    const parentId = item[parentKey];
    if (parentId != null && map.has(parentId)) {
      map.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });

  // recursive flatten
  const result = [];

  function flatten(nodes, level) {
    nodes.forEach(node => {
      const isLeaf = node.children.length === 0;
      result.push({id: node.id, label: node.label, level, isLeaf});
      if (!isLeaf) {
        flatten(node.children, level + 1);
      }
    });
  }

  flatten(roots, 0);

  return result;
}

/**
 * GroupedSelect component that accepts a flat list of items, builds
 * a tree hierarchy internally, and renders a single flat select
 * with indented items. Only leaf nodes are selectable.
 *
 * @param {Array} items - Flat list of items
 * @param {string} value - Currently selected value (leaf node's id)
 * @param {function} onChange - Callback when selection changes, receives the selected id or ""
 * @param {string} idKey - Property name for the unique identifier (default: "id")
 * @param {string} labelKey - Property name for the display label (default: "label")
 * @param {string} parentKey - Property name for the parent reference (default: "parent")
 * @param {string} label - Label for the select field
 * @param {boolean} disabled - Disable the select
 * @param {boolean} required - Mark as required
 * @param {string} error - Error flag
 * @param {string} helperText - Helper/error text to display below the select
 */
const GroupedSelect = ({
  items = [],
  value,
  onChange,
  idKey = "id",
  labelKey = "label",
  parentKey = "parent",
  className,
  label,
  disabled,
  required,
  error,
  helperText,
  id,
  sx,
  ...props
}) => {
  const language = useSelector(languageSelector);
  const languages = useSelector(languagesSelector);

  const options = useMemo(
    () => buildAndFlatten(items, idKey, labelKey, parentKey),
    [items, idKey, labelKey, parentKey]
  );

  const handleChange = useCallback(
    event => {
      onChange(event.target.value);
    },
    [onChange]
  );

  return (
    <FormControl
      fullWidth
      required={required}
      error={error}
      disabled={disabled}
      variant="outlined"
      className={`grouped-select ${className ? className : ""}`}
      sx={sx}
    >
      {label && <InputLabel className="grouped-select__label">{label}</InputLabel>}
      <Select
        id={id}
        className="grouped-select__select"
        label={label}
        value={value || ""}
        onChange={handleChange}
        variant="outlined"
        {...props}
      >
        {options.map(option => (
          <MenuItem
            key={option.id}
            value={option.isLeaf ? option.id : undefined}
            className={`grouped-select__option ${option.isLeaf ? "grouped-select__option--leaf" : "grouped-select__option--group"}`}
            sx={
              option.isLeaf
                ? {
                    paddingLeft: 2 + option.level * 2
                  }
                : {
                    paddingLeft: 2 + option.level * 2,
                    pointerEvents: "none",
                    cursor: "default",
                    opacity: 0.8,
                    fontStyle: "italic"
                  }
            }
          >
            <ListItemText primary={localizeI18nObj(option.label, language, languages) || option.id} />
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText className="grouped-select__helper-text">{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default GroupedSelect;
